import React, { useState } from "react";
import { Button as ReactstrapButton } from "reactstrap";
import { Button, Tag, Space, Badge, Tooltip, message } from "antd";
import type { TableColumnsType } from "antd";
import { Link, useNavigate, useLocation } from "react-router-dom";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import { Icon } from "../../components/Component";
import TransactionFlowDefaultTemplate from "../others/transactionFlowDefaultTemplate";
import WorkBoqAside from "./workBoqAside";
import workBoqApis from "../../api/WorkOrderManagement/WorkBOQ";

interface WorkBoqData {
  workBoqId: number;
  workBoqSequenceId: string;
  boqTitle: string;
  boqDescription: string;
  siteId: number;
  projectId: number;
  contractorId: number;
  completionAlertsFrom: number;
  segmentLevel: string;
  status: "Draft" | "Pending Approval" | "Approved" | "Rejected";
  approvalRequired: boolean;
  approvalStatus: string;
  createdAt: string;
  approvals: any[];
  // Additional fields that might come from other API calls or be computed
  siteName?: string;
  contractor?: string;
  totalLines?: number;
  createdBy?: string;
}

const WorkBoqList: React.FC = () => {
  const [sm, setSm] = useState(false);
  const [refreshToggle, setRefreshToggle] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Get flowType from URL to determine column label
  const searchParams = new URLSearchParams(location.search);
  const flowType = searchParams.get("flowType");
  const isClientFlow = flowType?.toLowerCase() === "client";
  const getStatusColor = (status: string) => {
    const statusColors: { [key: string]: string } = {
      Draft: "default",
      "Pending Approval": "processing",
      Approved: "success",
      Rejected: "error",
      Submitted: "processing",
    };
    return statusColors[status] || "default";
  };

  const handleEdit = (record: WorkBoqData) => {
    // Read flowType from URL query parameters
    const searchParams = new URLSearchParams(location.search);
    const flowType = searchParams.get("flowType");
    const flowTypeQuery = flowType ? `&flowType=${flowType}` : "";
    // Navigate to edit mode
    navigate(`/work-boq-creation?edit=${record.workBoqId}${flowTypeQuery}`);
  };

  const handleView = (record: WorkBoqData) => {
    // Read flowType from URL query parameters
    const searchParams = new URLSearchParams(location.search);
    const flowType = searchParams.get("flowType");
    const flowTypeQuery = flowType ? `?flowType=${flowType}` : "";
    // Navigate to view details
    navigate(`/work-boq-view/${record.workBoqId}${flowTypeQuery}`);
  };

  const handleDelete = (record: WorkBoqData) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        // API call would go here to delete the record
        Swal.fire("Deleted!", "BOQ has been deleted.", "success");
      }
    });
  };

  const handleSubmitForApproval = async (record: WorkBoqData) => {
    Swal.fire({
      title: "Submit for Approval",
      text: "Are you sure you want to submit this BOQ?",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, submit it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          // Create FormData with workBoqId and status
          const formData = new FormData();
          formData.append("workBoqId", record.workBoqId.toString());
          formData.append("status", "Submitted");

          console.log("Submitting BOQ with FormData:");
          const entries = Array.from(formData.entries());
          entries.forEach(([key, value]) => {
            console.log(key, value);
          });

          // Call the update API
          const response: any = await workBoqApis.updateWorkBoq(
            record.workBoqId.toString(),
            formData
          );

          if (response && response.status) {
            message.success("BOQ submitted successfully!");
            Swal.fire({
              icon: "success",
              title: "Submitted!",
              text:
                response?.data?.message ||
                "BOQ has been submitted for approval.",
              showConfirmButton: true,
              confirmButtonText: "OK",
            }).then(() => {
              // Trigger refresh by toggling the refresh state
              setRefreshToggle(!refreshToggle);
            });
          } else {
            throw new Error(response?.message || "Failed to submit BOQ");
          }
        } catch (error: any) {
          console.error("Error submitting BOQ:", error);
          message.error("Failed to submit BOQ");
          Swal.fire({
            icon: "error",
            title: "Error",
            text: error.message || "Failed to submit BOQ. Please try again.",
          });
        }
      }
    });
  };

  const columns: TableColumnsType<WorkBoqData> = [
    {
      title: "BOQ ID",
      dataIndex: "workBoqSequenceId",
      key: "workBoqSequenceId",
      width: 250,
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: "BOQ Title",
      dataIndex: "boqTitle",
      key: "boqTitle",
      width: 250,
      render: (text, record) => {
        const searchParams = new URLSearchParams(location.search);
        const flowType = searchParams.get("flowType");
        const flowTypeQuery = flowType ? `?flowType=${flowType}` : "";
        return (
          <div>
            <Tooltip title={text}>
              <Link
                to={`/work-boq-view/${record.workBoqId}${flowTypeQuery}`}
                className="fw-bold text-primary"
                style={{
                  display: "block",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "220px",
                }}
              >
                {text}
              </Link>
            </Tooltip>
            {/* <div className="text-muted small mt-1">{record.boqDescription}</div> */}
            {record.siteName && (
              <div className="text-muted small mt-1">
                Site: {record.siteName}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "Work Level",
      dataIndex: "segmentLevel",
      key: "segmentLevel",
      width: 120,
      render: (text) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: isClientFlow ? "Client" : "Contractor",
      dataIndex: "contractorName",
      key: "contractorName",
      width: 180,
      render: (text, record) => (text ? text : `N/A`),
    },
    // {
    //   title: "Project/Site",
    //   key: "projectSite",
    //   width: 120,
    //   render: (_, record) => (
    //     <div>
    //       <div>Project: {record.projectId || "N/A"}</div>
    //       <div>Site: {record.siteId}</div>
    //     </div>
    //   ),
    // },
    // {
    //   title: "Completion Alert",
    //   dataIndex: "completionAlertsFrom",
    //   key: "completionAlertsFrom",
    //   width: 120,
    //   align: "center",
    //   render: (text) => <Badge color="warning">{text}%</Badge>,
    // },
    {
      title: "Status",
      key: "statusGroup",
      width: 160,
      render: (_, record) => (
        <div>
          <Tag color={getStatusColor(record.status)}>{record.status}</Tag>
          {/* {record.approvalRequired && (
            <div className="mt-1">
              <Tag
                color={
                  record.approvalStatus === "Draft" ? "default" : "processing"
                }
              >
                {record.approvalStatus}
              </Tag>
            </div>
          )} */}
        </div>
      ),
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 220,
      render: (text, record: any) => (
        <div>
          <div>
            {dayjs(text).format("DD-MMM-YYYY")} {dayjs(text).format("HH:mm")}
          </div>
          <small className="text-muted">
            {record.createdByName && ` by ${record.createdByName}`}
          </small>
        </div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 200,
      render: (_, record) => (
        <Space>
          {/* <Button
            type="link"
            size="small"
            icon={<Icon name="eye" />}
            onClick={() => handleView(record)}
          >
            View
          </Button> */}

          {record.status === "Draft" && (
            <>
              <Button
                type="link"
                size="small"
                icon={<Icon name="edit" />}
                onClick={() => handleEdit(record)}
              >
                Edit
              </Button>
              <Button
                type="link"
                size="small"
                icon={<Icon name="send" />}
                onClick={() => handleSubmitForApproval(record)}
              >
                Submit
              </Button>
            </>
          )}

          {/* {(record.status === "Draft" || record.status === "Rejected") && (
            <Button
              type="link"
              size="small"
              danger
              icon={<Icon name="trash" />}
              onClick={() => handleDelete(record)}
            >
              Delete
            </Button>
          )} */}
        </Space>
      ),
    },
  ];

  return (
    <TransactionFlowDefaultTemplate
      moduleKey="work-boq-list"
      title="BOQ List"
      columns={columns}
      refresh={refreshToggle}
      sideNavigation={
        <div
          className={`card-aside card-aside-left user-aside toggle-slide toggle-slide-left toggle-break-lg ${
            sm ? "content-active" : ""
          }`}
        >
          <WorkBoqAside updateSm={setSm} sm={sm} />
        </div>
      }
      // topRightJSX={
      //   <Link to="/work-boq-creation">
      //     <ReactstrapButton color="primary">
      //       <Icon name="plus" />
      //       Create Work BOQ
      //     </ReactstrapButton>
      //   </Link>
      // }
      extra={{
        tableScrollX: 1400,
        excelExport: true,
      }}
    />
  );
};

export default WorkBoqList;
